import { Icon } from '../Icon';

export interface VBCCredentialsProps {
  licenseNumber: string | null;
  licenseType: string | null;
  licenseExpiry: string | null;
  insuranceProvider: string | null;
  insurancePolicyNumber: string | null;
  insuranceCoverageAmount: string | null;
  insuranceExpiry: string | null;
}

function formatDate(date: Date | string): string {
  const d = date instanceof Date ? date : new Date(date);
  return d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
}

/**
 * Credentials section — license and insurance info. Server Component.
 */
export function VBCCredentials({
  licenseNumber,
  licenseType,
  licenseExpiry,
  insuranceProvider,
  insurancePolicyNumber,
  insuranceCoverageAmount,
  insuranceExpiry,
}: VBCCredentialsProps) {
  const hasCredentials = licenseNumber || insuranceProvider;
  if (!hasCredentials) return null;

  return (
    <section
      className="mb-10 rounded-xl border p-6"
      style={{
        borderColor: 'var(--brand-card-border)',
        boxShadow: 'var(--shadow-resting)',
        backgroundColor: 'var(--brand-card-bg)',
      }}
    >
      <h2
        className="overline mb-5"
        style={{ color: 'var(--brand-primary, #014F8A)' }}
      >
        Credentials
      </h2>
      <div className="grid gap-5 sm:grid-cols-2">
        {/* License */}
        {licenseNumber && (
          <div
            className="rounded-xl border p-5"
            style={{
              borderColor: 'var(--brand-card-border)',
              backgroundColor: 'var(--brand-surface-subtle)',
            }}
          >
            <div className="mb-3 flex items-center gap-2.5">
              <div
                className="flex h-8 w-8 items-center justify-center rounded-lg"
                style={{ backgroundColor: 'color-mix(in srgb, var(--brand-primary) 8%, transparent)' }}
              >
                <Icon
                  name="description"
                  size={15}
                  style={{ color: 'var(--brand-primary, #014F8A)' }}
                />
              </div>
              <span
                className="font-semibold"
                style={{
                  color: 'var(--brand-text)',
                  fontSize: '14px',
                  letterSpacing: '-0.01em',
                }}
              >
                License
              </span>
            </div>
            <dl className="space-y-2">
              <div className="flex justify-between">
                <dt
                  style={{
                    color: 'var(--brand-input-placeholder)',
                    fontSize: '13px',
                  }}
                >
                  Number
                </dt>
                <dd
                  style={{
                    color: 'var(--brand-text-muted)',
                    fontSize: '13px',
                    fontWeight: 500,
                  }}
                >
                  {licenseNumber}
                </dd>
              </div>
              {licenseType && (
                <div className="flex justify-between">
                  <dt
                    style={{
                      color: 'var(--brand-input-placeholder)',
                      fontSize: '13px',
                    }}
                  >
                    Type
                  </dt>
                  <dd
                    style={{
                      color: 'var(--brand-text-muted)',
                      fontSize: '13px',
                      fontWeight: 500,
                    }}
                  >
                    {licenseType}
                  </dd>
                </div>
              )}
              {licenseExpiry && (
                <div className="flex items-center justify-between">
                  <dt
                    style={{
                      color: 'var(--brand-input-placeholder)',
                      fontSize: '13px',
                    }}
                  >
                    Valid Through
                  </dt>
                  <dd className="flex items-center gap-1.5">
                    <Icon
                      name="calendar_today"
                      size={12}
                      style={{ color: 'var(--brand-input-placeholder)' }}
                    />
                    <span
                      style={{
                        color: 'var(--brand-text-muted)',
                        fontSize: '13px',
                        fontWeight: 500,
                      }}
                    >
                      {formatDate(licenseExpiry)}
                    </span>
                  </dd>
                </div>
              )}
            </dl>
          </div>
        )}

        {/* Insurance */}
        {insuranceProvider && (
          <div
            className="rounded-xl border p-5"
            style={{
              borderColor: 'var(--brand-card-border)',
              backgroundColor: 'var(--brand-surface-subtle)',
            }}
          >
            <div className="mb-3 flex items-center gap-2.5">
              <div
                className="flex h-8 w-8 items-center justify-center rounded-lg"
                style={{ backgroundColor: 'color-mix(in srgb, var(--brand-primary) 8%, transparent)' }}
              >
                <Icon
                  name="gpp_maybe"
                  size={15}
                  style={{ color: 'var(--brand-primary, #014F8A)' }}
                />
              </div>
              <span
                className="font-semibold"
                style={{
                  color: 'var(--brand-text)',
                  fontSize: '14px',
                  letterSpacing: '-0.01em',
                }}
              >
                Insurance
              </span>
            </div>
            <dl className="space-y-2">
              <div className="flex justify-between">
                <dt
                  style={{
                    color: 'var(--brand-input-placeholder)',
                    fontSize: '13px',
                  }}
                >
                  Provider
                </dt>
                <dd
                  style={{
                    color: 'var(--brand-text-muted)',
                    fontSize: '13px',
                    fontWeight: 500,
                  }}
                >
                  {insuranceProvider}
                </dd>
              </div>
              {insurancePolicyNumber && (
                <div className="flex justify-between">
                  <dt
                    style={{
                      color: 'var(--brand-input-placeholder)',
                      fontSize: '13px',
                    }}
                  >
                    Policy #
                  </dt>
                  <dd
                    style={{
                      color: 'var(--brand-text-muted)',
                      fontSize: '13px',
                      fontWeight: 500,
                    }}
                  >
                    {insurancePolicyNumber}
                  </dd>
                </div>
              )}
              {insuranceCoverageAmount && (
                <div className="flex justify-between">
                  <dt
                    style={{
                      color: 'var(--brand-input-placeholder)',
                      fontSize: '13px',
                    }}
                  >
                    Coverage
                  </dt>
                  <dd
                    style={{
                      color: 'var(--brand-text-muted)',
                      fontSize: '13px',
                      fontWeight: 500,
                    }}
                  >
                    ${Number(insuranceCoverageAmount).toLocaleString()}
                  </dd>
                </div>
              )}
              {insuranceExpiry && (
                <div className="flex items-center justify-between">
                  <dt
                    style={{
                      color: 'var(--brand-input-placeholder)',
                      fontSize: '13px',
                    }}
                  >
                    Valid Through
                  </dt>
                  <dd className="flex items-center gap-1.5">
                    <Icon
                      name="calendar_today"
                      size={12}
                      style={{ color: 'var(--brand-input-placeholder)' }}
                    />
                    <span
                      style={{
                        color: 'var(--brand-text-muted)',
                        fontSize: '13px',
                        fontWeight: 500,
                      }}
                    >
                      {formatDate(insuranceExpiry)}
                    </span>
                  </dd>
                </div>
              )}
            </dl>
          </div>
        )}
      </div>
    </section>
  );
}
